import { Plus, Trash2 } from "lucide-react";
import { useId } from "react";

import type {
  AlertChannel,
  AlertDestinationRegistry,
  AlertDestinationRegistryEntry,
  AlertFlag,
  AlertSeverity,
} from "@/api/library";
import {
  ALERT_CHANNELS,
  ALERT_FLAGS,
  ALERT_SEVERITIES,
  CHANNEL_LABELS,
  FLAG_LABELS,
  SEVERITY_LABELS,
  comboPath,
  destinationPath,
  domainPath,
  newDraftCombo,
  newDraftDestination,
  newDraftDomain,
  routingPath,
  rulePath,
  templatePath,
  type DraftAlertMatrix,
  type DraftCombo,
  type DraftDestination,
  type DraftDomain,
  type DraftRouting,
  type MatrixProblem,
} from "@/lib/alertMatrix";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { Input, Label, Select, Textarea } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { useFieldAnchor } from "@/ui/validation/FieldAnchorContext";

/**
 * The authoring surface for an alert matrix (#35).
 *
 * Presentational and total, like #33's `PartsEditor`: it owns no draft and
 * performs no request, so its whole behaviour is a function of its props.
 *
 * THERE IS NO `readOnly` PROP, and that is the design rather than an omission.
 * A read-only matrix is not this form with its controls turned off — it is
 * `VersionContent`, which renders the same content with no controls at all.
 * `AlertMatrixAssetEditor` chooses between them, so "a seeded or locked-floor
 * matrix shows zero enabled authoring controls" is structural: there are no
 * controls in the tree to enable. #33's editor had to take the other route
 * because a prompt must stay selectable and copyable in a textarea; a matrix
 * reads better as prose than as a disabled form.
 *
 * WHAT THE CONTROLS MIRROR. A domain carries at most one rule per flag and a
 * matrix at most one routing entry per severity, both by contract — so the flag
 * IS the slot and the severity IS the row, and "no rule" is an empty select
 * rather than a list a clinician has to remember to prune. Every severity gets
 * a routing row whether it is routed or not, because turning routing off is an
 * edit they must be able to make and see.
 *
 * DESTINATIONS ARE REFERENCES. The control accepts a slug and says, in the
 * field's own description, that addresses, numbers, URLs and credentials live
 * outside the clinical library. This is the prototype defect #35 exists to
 * correct: the Lovable editor edited destination values as plain text, and
 * content is immutable once published. #637 adds the refusal that makes the
 * description true for values that fit the slug shape anyway. The separate
 * server registry read offers active references; the write remains authoritative.
 */

function problemsAt(
  problems: readonly MatrixProblem[],
  path: string,
): MatrixProblem[] {
  return problems.filter((problem) => problem.path === path);
}

/** A message list wired to its control through `aria-describedby`. */
function Messages({
  id,
  tone,
  messages,
}: {
  id: string;
  tone: "problem" | "warning";
  messages: readonly MatrixProblem[];
}) {
  if (messages.length === 0) {
    return null;
  }
  return (
    // The live region is the WRAPPER, not each item. A `role` on an `<li>`
    // replaces its implicit `listitem`, which leaves the `<ul>` holding children
    // that are not list items — axe's `list` rule, and a screen reader that stops
    // announcing "list of 2" with it. Announcing the group once is also the
    // better behaviour: three refusals used to fire three assertive
    // interruptions.
    //
    // A blocking refusal interrupts; a privacy warning is advice that never
    // stops the save, so it is announced politely instead.
    <div role={tone === "problem" ? "alert" : "status"}>
      <ul id={id} className="mt-1 space-y-1">
        {messages.map((message, position) => (
          <li
            // The caller may combine independently attributed client and
            // server findings, and neither `MatrixProblem` nor this component
            // promises that path plus prose is unique. The render position is
            // therefore the final discriminator, as in the parts editors. The
            // item itself is stateless text, so a reordered message cannot
            // carry child state to a different refusal.
            key={`${position}:${message.path}:${message.message}`}
            className={
              tone === "problem"
                ? "text-xs text-destructive"
                : "text-xs text-warning-foreground"
            }
          >
            {tone === "warning" ? (
              <Badge tone="warning" className="mr-2">
                Privacy
              </Badge>
            ) : null}
            {message.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DomainRow({
  domain,
  index,
  problems,
  onChange,
  onRemove,
}: {
  domain: DraftDomain;
  index: number;
  problems: readonly MatrixProblem[];
  onChange: (patch: Partial<DraftDomain>) => void;
  onRemove: () => void;
}) {
  const path = domainPath(index);
  const { ref: anchorRef, id: anchorId } = useFieldAnchor(path);
  const nameId = useId();
  const problemId = useId();
  const rowProblems = problemsAt(problems, path);
  const invalid = rowProblems.length > 0;

  return (
    <li className="rounded-md border border-border bg-card p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[10rem] flex-1 space-y-1.5">
          <Label htmlFor={anchorId}>Domain key</Label>
          <Input
            id={anchorId}
            ref={anchorRef}
            className="font-mono"
            value={domain.key}
            placeholder="mood"
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? problemId : undefined}
            onChange={(event) => onChange({ key: event.target.value })}
          />
        </div>
        <div className="min-w-[10rem] flex-1 space-y-1.5">
          <Label htmlFor={nameId}>Domain name</Label>
          <Input
            id={nameId}
            value={domain.displayName}
            placeholder="Mood"
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? problemId : undefined}
            onChange={(event) => onChange({ displayName: event.target.value })}
          />
        </div>
        <IconButton
          label={`Remove domain ${domain.key || index + 1}`}
          icon={<Trash2 className="h-4 w-4" />}
          onClick={onRemove}
        />
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {ALERT_FLAGS.map((flag) => (
          <ThresholdControl
            key={flag}
            flag={flag}
            index={index}
            rule={domain.rules[flag]}
            problems={problems}
            onChange={(patch) =>
              onChange({
                rules: {
                  ...domain.rules,
                  [flag]: { ...domain.rules[flag], ...patch },
                },
              })
            }
          />
        ))}
      </div>

      <Messages id={problemId} tone="problem" messages={rowProblems} />
    </li>
  );
}

function ThresholdControl({
  flag,
  index,
  rule,
  problems,
  onChange,
}: {
  flag: AlertFlag;
  index: number;
  rule: DraftDomain["rules"][AlertFlag];
  problems: readonly MatrixProblem[];
  onChange: (patch: Partial<DraftDomain["rules"][AlertFlag]>) => void;
}) {
  const path = rulePath(index, flag);
  const { ref: anchorRef, id: anchorId } = useFieldAnchor(path);
  const countId = useId();
  const labelId = useId();
  const problemId = useId();
  const ruleProblems = problemsAt(problems, path);
  const invalid = ruleProblems.length > 0;
  const active = rule.severity !== "";

  return (
    <div className="space-y-1.5 rounded-md border border-border p-3">
      <Label htmlFor={anchorId}>{FLAG_LABELS[flag]} raise</Label>
      <Select
        id={anchorId}
        ref={anchorRef}
        value={rule.severity}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? problemId : undefined}
        onChange={(event) =>
          onChange({ severity: event.target.value as AlertSeverity | "" })
        }
      >
        <option value="">No alert</option>
        {ALERT_SEVERITIES.map((severity) => (
          <option key={severity} value={severity}>
            {SEVERITY_LABELS[severity]}
          </option>
        ))}
      </Select>
      {active ? (
        <>
          <div className="space-y-1.5">
            <Label htmlFor={countId}>
              {FLAG_LABELS[flag]} needed
            </Label>
            <Input
              id={countId}
              type="number"
              min={1}
              inputMode="numeric"
              value={rule.minCount}
              aria-invalid={invalid || undefined}
              aria-describedby={invalid ? problemId : undefined}
              onChange={(event) => onChange({ minCount: event.target.value })}
            />
          </div>
          {/* On screen for the same reason the cross-domain rule's label is:
              it describes the threshold beside it, and a threshold the
              clinician can change is a label the clinician has to be able to
              change with it. Hidden, it outlives the count it describes. */}
          <div className="space-y-1.5">
            <Label htmlFor={labelId}>
              {FLAG_LABELS[flag]} label (optional)
            </Label>
            <Input
              id={labelId}
              value={rule.label}
              placeholder="Any red flag"
              aria-invalid={invalid || undefined}
              aria-describedby={invalid ? problemId : undefined}
              onChange={(event) => onChange({ label: event.target.value })}
            />
          </div>
        </>
      ) : null}
      <Messages id={problemId} tone="problem" messages={ruleProblems} />
    </div>
  );
}

function ComboRow({
  combo,
  index,
  problems,
  onChange,
  onRemove,
}: {
  combo: DraftCombo;
  index: number;
  problems: readonly MatrixProblem[];
  onChange: (patch: Partial<DraftCombo>) => void;
  onRemove: () => void;
}) {
  const path = comboPath(index);
  const { ref: anchorRef, id: anchorId } = useFieldAnchor(path);
  const countId = useId();
  const severityId = useId();
  const labelId = useId();
  const problemId = useId();
  const rowProblems = problemsAt(problems, path);
  const invalid = rowProblems.length > 0;

  return (
    <li className="rounded-md border border-border bg-card p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[8rem] space-y-1.5">
          <Label htmlFor={anchorId}>Flag colour</Label>
          <Select
            id={anchorId}
            ref={anchorRef}
            value={combo.flag}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? problemId : undefined}
            onChange={(event) =>
              onChange({ flag: event.target.value as AlertFlag })
            }
          >
            {ALERT_FLAGS.map((flag) => (
              <option key={flag} value={flag}>
                {FLAG_LABELS[flag]}
              </option>
            ))}
          </Select>
        </div>
        <div className="min-w-[8rem] space-y-1.5">
          <Label htmlFor={countId}>Domains needed</Label>
          <Input
            id={countId}
            type="number"
            min={2}
            inputMode="numeric"
            value={combo.minDomains}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? problemId : undefined}
            onChange={(event) => onChange({ minDomains: event.target.value })}
          />
        </div>
        <div className="min-w-[8rem] space-y-1.5">
          <Label htmlFor={severityId}>Raises</Label>
          <Select
            id={severityId}
            value={combo.severity}
            onChange={(event) =>
              onChange({ severity: event.target.value as AlertSeverity })
            }
          >
            {ALERT_SEVERITIES.map((severity) => (
              <option key={severity} value={severity}>
                {SEVERITY_LABELS[severity]}
              </option>
            ))}
          </Select>
        </div>
        <div className="min-w-[12rem] flex-1 space-y-1.5">
          <Label htmlFor={labelId}>Label (optional)</Label>
          <Input
            id={labelId}
            value={combo.label}
            placeholder="Two domains amber"
            onChange={(event) => onChange({ label: event.target.value })}
          />
        </div>
        <IconButton
          label={`Remove cross-domain rule ${index + 1}`}
          icon={<Trash2 className="h-4 w-4" />}
          onClick={onRemove}
        />
      </div>
      <Messages id={problemId} tone="problem" messages={rowProblems} />
    </li>
  );
}

function DestinationRow({
  destination,
  index,
  problems,
  onChange,
  onRemove,
  options,
  registryReady,
}: {
  destination: DraftDestination;
  index: number;
  problems: readonly MatrixProblem[];
  onChange: (ref: string) => void;
  onRemove: () => void;
  options: readonly AlertDestinationRegistryEntry[];
  registryReady: boolean;
}) {
  const path = destinationPath(index);
  const { ref: anchorRef, id: anchorId } = useFieldAnchor(path);
  const problemId = useId();
  const rowProblems = problemsAt(problems, path);
  const invalid = rowProblems.length > 0;
  const current = options.find((entry) => entry.ref === destination.ref);
  const selectable = options.filter((entry) => entry.lifecycle === "active");

  return (
    <li className="space-y-1.5">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[14rem] flex-1 space-y-1.5">
          <Label htmlFor={anchorId}>Destination reference {index + 1}</Label>
          <Select
            id={anchorId}
            ref={anchorRef}
            className="font-mono"
            value={destination.ref}
            disabled={!registryReady}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? problemId : undefined}
            onChange={(event) => onChange(event.target.value)}
          >
            {destination.ref === "" ? (
              <option value="">Choose an approved destination</option>
            ) : null}
            {destination.ref !== "" && current?.lifecycle !== "active" ? (
              <option value={destination.ref}>
                {destination.ref} · unavailable
              </option>
            ) : null}
            {selectable.map((entry) => (
              <option key={entry.ref} value={entry.ref}>
                {entry.ref} · {entry.kind}
              </option>
            ))}
          </Select>
        </div>
        <IconButton
          label={`Remove destination reference ${index + 1}`}
          icon={<Trash2 className="h-4 w-4" />}
          onClick={onRemove}
        />
      </div>
      <Messages id={problemId} tone="problem" messages={rowProblems} />
    </li>
  );
}

function TemplateField({
  severity,
  field,
  label,
  description,
  value,
  multiline,
  problems,
  warnings,
  onChange,
}: {
  severity: AlertSeverity;
  field: "emailSubject" | "emailBody" | "smsBody";
  label: string;
  description: string;
  value: string;
  multiline: boolean;
  problems: readonly MatrixProblem[];
  warnings: readonly MatrixProblem[];
  onChange: (value: string) => void;
}) {
  const path = templatePath(severity, field);
  const { ref: anchorRef, id: anchorId } = useFieldAnchor(path);
  const problemId = useId();
  const warningId = useId();
  const descriptionId = useId();
  const fieldProblems = problemsAt(problems, path);
  const fieldWarnings = problemsAt(warnings, path);
  const invalid = fieldProblems.length > 0;
  const describedBy = [
    invalid ? problemId : null,
    fieldWarnings.length > 0 ? warningId : null,
    descriptionId,
  ]
    .filter((id): id is string => id !== null)
    .join(" ");

  return (
    <div className="space-y-1.5">
      <Label htmlFor={anchorId}>{label}</Label>
      {multiline ? (
        <Textarea
          id={anchorId}
          ref={anchorRef}
          rows={3}
          value={value}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <Input
          id={anchorId}
          ref={anchorRef}
          value={value}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      <p id={descriptionId} className="text-xs text-muted-foreground">
        {description}
      </p>
      <Messages id={problemId} tone="problem" messages={fieldProblems} />
      <Messages id={warningId} tone="warning" messages={fieldWarnings} />
    </div>
  );
}

function RoutingRow({
  route,
  problems,
  warnings,
  onChange,
}: {
  route: DraftRouting;
  problems: readonly MatrixProblem[];
  warnings: readonly MatrixProblem[];
  onChange: (patch: Partial<DraftRouting>) => void;
}) {
  const path = routingPath(route.severity);
  const { ref: anchorRef, id: anchorId } = useFieldAnchor(path);
  const problemId = useId();
  const rowProblems = problemsAt(problems, path);

  function toggle(channel: AlertChannel) {
    onChange({
      channels: route.channels.includes(channel)
        ? route.channels.filter((entry) => entry !== channel)
        : ALERT_CHANNELS.filter(
            (entry) => entry === channel || route.channels.includes(entry),
          ),
    });
  }

  return (
    <li
      ref={anchorRef}
      id={anchorId}
      tabIndex={-1}
      // Named, because every control inside it — three identically-labelled
      // channel toggles and up to three templates — is only unambiguous
      // relative to the severity that owns it.
      aria-label={`${SEVERITY_LABELS[route.severity]} severity routing`}
      className="space-y-3 rounded-md border border-border bg-card p-4"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-foreground">
          {SEVERITY_LABELS[route.severity]} severity
        </span>
        <span className="text-sm text-muted-foreground">dispatches to</span>
        {ALERT_CHANNELS.map((channel) => (
          <Button
            key={channel}
            size="sm"
            variant={route.channels.includes(channel) ? "primary" : "outline"}
            aria-pressed={route.channels.includes(channel)}
            onClick={() => toggle(channel)}
          >
            {CHANNEL_LABELS[channel]}
          </Button>
        ))}
        {route.channels.length === 0 ? (
          <Badge tone="outline">Not routed</Badge>
        ) : null}
      </div>

      {route.channels.includes("email") ? (
        <>
          <TemplateField
            severity={route.severity}
            field="emailSubject"
            label={`${SEVERITY_LABELS[route.severity]} email subject`}
            description="Placeholders: {domain}, {rule}, {severity}, {patient_name}."
            value={route.emailSubject}
            multiline={false}
            problems={problems}
            warnings={warnings}
            onChange={(value) => onChange({ emailSubject: value })}
          />
          <TemplateField
            severity={route.severity}
            field="emailBody"
            label={`${SEVERITY_LABELS[route.severity]} email body`}
            description="Placeholders: {domain}, {rule}, {severity}, {patient_name}."
            value={route.emailBody}
            multiline
            problems={problems}
            warnings={warnings}
            onChange={(value) => onChange({ emailBody: value })}
          />
        </>
      ) : null}

      {route.channels.includes("sms") ? (
        <TemplateField
          severity={route.severity}
          field="smsBody"
          label={`${SEVERITY_LABELS[route.severity]} SMS message`}
          description="Placeholders: {domain}, {rule}, {severity}, {patient_name}."
          value={route.smsBody}
          multiline
          problems={problems}
          warnings={warnings}
          onChange={(value) => onChange({ smsBody: value })}
        />
      ) : null}

      {route.channels.includes("webhook") ? (
        <p className="text-xs text-muted-foreground">
          The webhook channel carries the structured alert; it has no authored
          message. Which endpoint receives it is a destination reference, not
          content.
        </p>
      ) : null}

      <Messages id={problemId} tone="problem" messages={rowProblems} />
    </li>
  );
}

export function AlertMatrixEditor({
  draft,
  onChange,
  problems = [],
  warnings = [],
  destinationRegistry,
  destinationRegistryState,
  onRetryDestinationRegistry,
}: {
  draft: DraftAlertMatrix;
  onChange: (draft: DraftAlertMatrix) => void;
  problems?: readonly MatrixProblem[];
  warnings?: readonly MatrixProblem[];
  destinationRegistry: AlertDestinationRegistry | null;
  destinationRegistryState: "pending" | "ready" | "error";
  onRetryDestinationRegistry: () => void;
}) {
  // Destructured at the call site, as `PartsEditor` does: the registry callback
  // is a ref, and reading it off a held object during render is what
  // `react-hooks/refs` exists to catch.
  const { ref: domainsRef, id: domainsId } = useFieldAnchor("content.domains");
  const { ref: combosRef, id: combosId } = useFieldAnchor("content.combos");
  const { ref: destinationsRef, id: destinationsId } = useFieldAnchor(
    "content.destinationRefs",
  );
  const { ref: routingRef, id: routingId } = useFieldAnchor("content.routing");
  const destinationOptions = destinationRegistry?.entries ?? [];
  const activeDestinations = destinationOptions.filter(
    (entry) => entry.lifecycle === "active",
  );

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Domains and thresholds
          </h3>
          <p className="text-xs text-muted-foreground">
            How many flags of a colour, within one domain, raise an alert and at
            which severity.
          </p>
        </div>
        <ol
          ref={domainsRef}
          id={domainsId}
          tabIndex={-1}
          aria-label="Alert domains"
          className="space-y-3"
        >
          {draft.domains.map((domain, index) => (
            <DomainRow
              key={domain.id}
              domain={domain}
              index={index}
              problems={problems}
              onChange={(patch) =>
                onChange({
                  ...draft,
                  domains: draft.domains.map((entry, position) =>
                    position === index ? { ...entry, ...patch } : entry,
                  ),
                })
              }
              onRemove={() =>
                onChange({
                  ...draft,
                  domains: draft.domains.filter(
                    (_, position) => position !== index,
                  ),
                })
              }
            />
          ))}
        </ol>
        <Messages
          id={`${domainsId}-problems`}
          tone="problem"
          messages={problemsAt(problems, "content.domains")}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            onChange({ ...draft, domains: [...draft.domains, newDraftDomain()] })
          }
        >
          <Plus aria-hidden="true" className="h-4 w-4" />
          Add domain
        </Button>
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Cross-domain rules
          </h3>
          <p className="text-xs text-muted-foreground">
            Escalation when the same colour appears across several domains at
            once.
          </p>
        </div>
        <ol
          ref={combosRef}
          id={combosId}
          tabIndex={-1}
          aria-label="Cross-domain escalation rules"
          className="space-y-3"
        >
          {draft.combos.map((combo, index) => (
            <ComboRow
              key={combo.id}
              combo={combo}
              index={index}
              problems={problems}
              onChange={(patch) =>
                onChange({
                  ...draft,
                  combos: draft.combos.map((entry, position) =>
                    position === index ? { ...entry, ...patch } : entry,
                  ),
                })
              }
              onRemove={() =>
                onChange({
                  ...draft,
                  combos: draft.combos.filter(
                    (_, position) => position !== index,
                  ),
                })
              }
            />
          ))}
        </ol>
        <Messages
          id={`${combosId}-problems`}
          tone="problem"
          messages={problemsAt(problems, "content.combos")}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            onChange({ ...draft, combos: [...draft.combos, newDraftCombo()] })
          }
        >
          <Plus aria-hidden="true" className="h-4 w-4" />
          Add cross-domain rule
        </Button>
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Destinations</h3>
          <p className="text-xs text-muted-foreground">
            References to configured destinations — never an address, a phone
            number, a URL or a credential. Those are held outside the clinical
            library, and a value typed here would land in immutable content that
            cannot be edited to remove it. Choose an active reference from the
            server-owned registry; project scope is checked again when a matrix
            is newly adopted or moved.
          </p>
        </div>
        {destinationRegistryState === "pending" ? (
          <p role="status" className="text-xs text-muted-foreground">
            Loading approved destinations…
          </p>
        ) : destinationRegistryState === "error" ? (
          <Notice
            role="alert"
            tone="destructive"
            action={
              <Button variant="outline" size="sm" onClick={onRetryDestinationRegistry}>
                Retry destinations
              </Button>
            }
          >
            Approved destinations are unavailable. Existing references stay
            visible and removable, but none can be added or changed.
          </Notice>
        ) : activeDestinations.length === 0 ? (
          <Notice role="status" tone="warning">
            This authority snapshot contains no active destinations for this
            environment. Existing references stay visible and removable.
          </Notice>
        ) : (
          <p className="text-xs text-muted-foreground">
            Registry {destinationRegistry?.version} · {activeDestinations.length}{" "}
            active {activeDestinations.length === 1 ? "reference" : "references"}
          </p>
        )}
        <ul
          ref={destinationsRef}
          id={destinationsId}
          tabIndex={-1}
          aria-label="Destination references"
          className="space-y-3"
        >
          {draft.destinations.map((destination, index) => (
            <DestinationRow
              key={destination.id}
              destination={destination}
              index={index}
              problems={problems}
              onChange={(ref) =>
                onChange({
                  ...draft,
                  destinations: draft.destinations.map((entry, position) =>
                    position === index ? { ...entry, ref } : entry,
                  ),
                })
              }
              onRemove={() =>
                onChange({
                  ...draft,
                  destinations: draft.destinations.filter(
                    (_, position) => position !== index,
                  ),
                })
              }
              options={destinationOptions}
              registryReady={destinationRegistryState === "ready"}
            />
          ))}
        </ul>
        <Messages
          id={`${destinationsId}-problems`}
          tone="problem"
          messages={problemsAt(problems, "content.destinationRefs")}
        />
        <Button
          variant="outline"
          size="sm"
          disabled={destinationRegistryState !== "ready" || activeDestinations.length === 0}
          onClick={() =>
            onChange({
              ...draft,
              destinations: [
                ...draft.destinations,
                {
                  ...newDraftDestination(),
                  ref: activeDestinations[0]?.ref ?? "",
                },
              ],
            })
          }
        >
          <Plus aria-hidden="true" className="h-4 w-4" />
          Add destination reference
        </Button>
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Routing and messages
          </h3>
          <p className="text-xs text-muted-foreground">
            Every severity a threshold can raise needs at least one channel, and
            a channel with no threshold behind it never fires. Turning a channel
            off drops its message from the next published version.
          </p>
        </div>
        <ol
          ref={routingRef}
          id={routingId}
          tabIndex={-1}
          aria-label="Severity routing"
          className="space-y-3"
        >
          {draft.routing.map((route, index) => (
            <RoutingRow
              key={route.severity}
              route={route}
              problems={problems}
              warnings={warnings}
              onChange={(patch) =>
                onChange({
                  ...draft,
                  routing: draft.routing.map((entry, position) =>
                    position === index ? { ...entry, ...patch } : entry,
                  ),
                })
              }
            />
          ))}
        </ol>
        <Messages
          id={`${routingId}-problems`}
          tone="problem"
          messages={problemsAt(problems, "content.routing")}
        />
      </section>

      <Messages
        id="alert-matrix-problems"
        tone="problem"
        messages={problemsAt(problems, "content")}
      />
    </div>
  );
}
